import React, { useMemo } from 'react';
import { useGetV2AdminOrganizationsQuery } from '@features/admin-v2/store/hooks';
import { Public as GlobalIcon } from '@mui/icons-material';
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';

interface OrganizationOption {
  id: string | null;
  name: string;
  isGlobal?: boolean;
}

interface OrganizationSelectorProps {
  /** Currently selected organization ID, null = Global context */
  selectedOrgId: string | null;
  /** Callback when organization selection changes */
  onOrgChange: (orgId: string | null, orgName: string | null) => void;
  /** Optional label for the selector */
  label?: string;
  /** Whether the selector is disabled */
  disabled?: boolean;
}

/**
 * OrganizationSelector Component
 *
 * Allows admins to switch between Global context and specific organizations.
 * Uses autocomplete for efficient searching with large org lists.
 */
export const OrganizationSelector: React.FC<OrganizationSelectorProps> = ({
  selectedOrgId,
  onOrgChange,
  label = 'Context',
  disabled = false,
}) => {
  // Fetch organizations list
  const { data: orgsData, isLoading } = useGetV2AdminOrganizationsQuery({
    perPage: 1000, // Fetch all orgs for autocomplete
  });

  // Build options list with Global as first option
  const options = useMemo<OrganizationOption[]>(() => {
    const globalOption: OrganizationOption = {
      id: null,
      name: 'Global Rules',
      isGlobal: true,
    };

    const orgOptions: OrganizationOption[] =
      orgsData?.items?.map((org: { organization_id: string; name: string }) => ({
        id: org.organization_id,
        name: org.name || org.organization_id,
      })) || [];

    return [globalOption, ...orgOptions];
  }, [orgsData]);

  // Find current selected option
  const selectedOption = useMemo(() => {
    return options.find(opt => opt.id === selectedOrgId) || options[0];
  }, [options, selectedOrgId]);

  return (
    <Autocomplete
      value={selectedOption}
      onChange={(_event, newValue) => {
        if (newValue) {
          onOrgChange(newValue.id, newValue.isGlobal ? null : newValue.name);
        }
      }}
      options={options}
      getOptionLabel={option => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      disabled={disabled}
      loading={isLoading}
      sx={{ minWidth: 280 }}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          size="small"
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...otherProps } = props as React.HTMLAttributes<HTMLLIElement> & {
          key: string;
        };
        return (
          <Box
            component="li"
            key={key}
            {...otherProps}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              ...(option.isGlobal && {
                borderBottom: '1px solid',
                borderColor: 'divider',
                pb: 1,
                mb: 1,
              }),
            }}
          >
            {option.isGlobal && <GlobalIcon fontSize="small" color="primary" />}
            <Typography
              variant="body2"
              sx={{
                fontWeight: option.isGlobal ? 600 : 400,
                color: option.isGlobal ? 'primary.main' : 'text.primary',
              }}
            >
              {option.name}
            </Typography>
          </Box>
        );
      }}
    />
  );
};

export default OrganizationSelector;
